/**
 * O seletor de vínculos — o componente mais usado da Criação do Mundo.
 *
 * <p>Regra que ele existe para impor (item 1 da spec): **campo de relação só aceita
 * ficha que já existe**. Não dá para criar uma criatura digitando o nome dela aqui,
 * porque isso encheria o mundo de duplicatas com grafias diferentes. Quando a ficha
 * ainda não existe, o atalho "criar" a cadastra de verdade e já a devolve vinculada.
 *
 * <p>Serve tanto para relação múltipla (vira lista de chips) quanto para referência
 * única (`unico`), porque a interação é a mesma: buscar entre o que existe e escolher.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { definicaoDe } from './esquema';
import { CapaAutenticada } from '../../comum/ImagemAutenticada';
import * as mundoApi from './mundoApi';
import type { Colecao, Resumo } from './tipos';

interface Props {
  alvo: Colecao;
  selecionados: Resumo[];
  aoMudar: (ids: number[]) => void;
  /** Referência única: escolher substitui em vez de somar. */
  unico?: boolean;
  /** Clicar num chip abre a ficha daquela entidade. */
  aoAbrir: (colecao: Colecao, id: number) => void;
  desabilitado?: boolean;
  /**
   * Oferecer o atalho "+ Criar", que cadastra a ficha de verdade na coleção alvo.
   *
   * <p>Verdadeiro por padrão, que é o comportamento de todas as fichas do mundo. A aba
   * Cenas o desliga: ela referencia o conteúdo e nunca o escreve, e um botão que cria
   * NPC a partir dali seria exatamente a porta dos fundos que ela não pode ter.
   */
  permitirCriar?: boolean;
}

export function SeletorDeRelacao({
  alvo,
  selecionados,
  aoMudar,
  unico,
  aoAbrir,
  desabilitado,
  permitirCriar = true,
}: Props) {
  const definicao = definicaoDe(alvo);
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);
  const [candidatos, setCandidatos] = useState<Resumo[]>([]);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const caixa = useRef<HTMLDivElement>(null);

  // A lista da coleção alvo é carregada uma vez, na primeira abertura, e filtrada
  // em memória. Para o tamanho de um mundo escrito à mão, buscar no servidor a cada
  // tecla só adicionaria latência.
  useEffect(() => {
    if (!aberto || candidatos.length > 0) return;
    let ativo = true;
    mundoApi
      .listar(alvo)
      .then((lista) => ativo && setCandidatos(lista))
      .catch((e) => ativo && setErro(e instanceof Error ? e.message : 'Falha ao buscar.'));
    return () => {
      ativo = false;
    };
  }, [aberto, alvo, candidatos.length]);

  // Clicar fora fecha — sem isto, a lista fica aberta atrás do resto da ficha.
  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  const jaEscolhidos = useMemo(
    () => new Set(selecionados.map((s) => s.id)),
    [selecionados]
  );

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return candidatos
      .filter((c) => !jaEscolhidos.has(c.id))
      .filter((c) => !termo || c.nome.toLowerCase().includes(termo))
      .slice(0, 40);
  }, [candidatos, busca, jaEscolhidos]);

  const nomeNovo = busca.trim();
  const podeCriar =
    permitirCriar &&
    nomeNovo.length > 1 &&
    !candidatos.some((c) => c.nome.toLowerCase() === nomeNovo.toLowerCase());

  function escolher(item: Resumo) {
    aoMudar(unico ? [item.id] : [...selecionados.map((s) => s.id), item.id]);
    setBusca('');
    setAberto(false);
  }

  function remover(id: number) {
    aoMudar(selecionados.filter((s) => s.id !== id).map((s) => s.id));
  }

  /** Cria a ficha de verdade na coleção alvo e já a devolve vinculada. */
  async function criarEVincular() {
    setCriando(true);
    setErro(null);
    try {
      const nova = await mundoApi.criar(alvo, { nome: nomeNovo });
      const resumo: Resumo = { colecao: alvo, id: nova.id, nome: nova.nome };
      setCandidatos((antes) => [...antes, resumo]);
      escolher(resumo);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível criar.');
    } finally {
      setCriando(false);
    }
  }

  return (
    <div className="rel" ref={caixa}>
      <div className="rel-chips">
        {selecionados.map((item) => (
          <span key={item.id} className="rel-chip">
            {item.imagem && <CapaAutenticada className="rel-mini" url={item.imagem} mini />}
            <button
              type="button"
              className="rel-nome"
              onClick={() => aoAbrir(item.colecao, item.id)}
              title={`Abrir ${item.nome}`}
            >
              {item.nome}
            </button>
            {item.nota && <span className="rel-nota">{item.nota}</span>}
            {!desabilitado && (
              <button
                type="button"
                className="rel-x"
                onClick={() => remover(item.id)}
                title="Desvincular"
              >
                ×
              </button>
            )}
          </span>
        ))}

      </div>

      {!desabilitado && (!unico || selecionados.length === 0) && (
        <div className="rel-busca">
          <input
            value={busca}
            placeholder={`Buscar em ${definicao.rotulo.toLowerCase()}…`}
            onChange={(e) => {
              setBusca(e.target.value);
              setAberto(true);
            }}
            onFocus={() => setAberto(true)}
          />

          {aberto && (
            <div className="rel-lista">
              {filtrados.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="rel-item"
                  onClick={() => escolher(item)}
                >
                  <CapaAutenticada className="rel-mini" url={item.imagem} mini />
                  <span className="rel-item-nome">{item.nome}</span>
                  {item.nota && <span className="rel-nota">{item.nota}</span>}
                </button>
              ))}

              {filtrados.length === 0 && !podeCriar && (
                <div className="rel-nada">
                  {candidatos.length === 0
                    ? `Nenhuma ficha em ${definicao.rotulo.toLowerCase()} ainda.`
                    : 'Nada encontrado.'}
                </div>
              )}

              {podeCriar && (
                <button
                  type="button"
                  className="rel-criar"
                  onClick={() => void criarEVincular()}
                  disabled={criando}
                >
                  {criando ? 'Criando…' : `+ Criar ${definicao.singular} "${nomeNovo}"`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {erro && <div className="rel-erro">{erro}</div>}
    </div>
  );
}
